import { createContext, useState, FC, ReactNode, useContext, useCallback } from "react";
import { AuthContext } from "./AuthContext";
import { addClient as addClientService, getClientsList, generateClientLink, getClientServiceResults } from "../utilities/api/clientsService";
import { Client, ServiceResultItem } from "../utilities/types/clients/types";

interface ClientsManagementContextValue {
  clients: Client[];
  selectedClient: Client | null;
  clientServices: ServiceResultItem[];
  loading: boolean;
  loadingServices: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalClients: number;
  perPage: number;
  servicesCurrentPage: number;
  servicesTotalPages: number;
  addClient: (clientData: { name: string }) => Promise<void>;
  fetchClients: (page?: number) => Promise<void>;
  fetchClientServices: (ref_id: string, page?: number) => Promise<void>;
  setSelectedClient: (client: Client | null) => void;
  resetError: () => void;
  getClientLink: (ref_id: string) => string;
}

export const ClientsManagementContext = createContext<ClientsManagementContextValue>({
  clients: [],
  selectedClient: null,
  clientServices: [],
  loading: false,
  loadingServices: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalClients: 0,
  perPage: 12,
  servicesCurrentPage: 1,
  servicesTotalPages: 1,
  addClient: async () => {},
  fetchClients: async () => {},
  fetchClientServices: async () => {},
  setSelectedClient: () => {},
  resetError: () => {},
  getClientLink: (ref_id: string) => "",
});

export const ClientsManagementProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { accessToken } = useContext(AuthContext);

  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientServices, setClientServices] = useState<ServiceResultItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [perPage, setPerPage] = useState(12);
  const [servicesCurrentPage, setServicesCurrentPage] = useState(1);
  const [servicesTotalPages, setServicesTotalPages] = useState(1);

  const fetchClients = useCallback(
    async (page: number = 1) => {
      if (!accessToken) {
        setError("No access token available");
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await getClientsList(accessToken, page);
        if (response.ok) {
          setClients(response.data.data);
          setCurrentPage(response.data.current_page);
          setTotalPages(response.data.last_page);
          setTotalClients(response.data.total);
          setPerPage(response.data.per_page);
        } else {
          setError(response.message || "Failed to fetch clients");
        }
      } catch (err: any) {
        console.error("Error fetching clients:", err);
        setError(err.message || "Failed to fetch clients");
      } finally {
        setLoading(false);
      }
    },
    [accessToken]
  );
  const addClient = useCallback(
    async (clientData: { name: string }) => {
      if (!accessToken) {
        setError("No access token available");
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await addClientService(accessToken, clientData);
        if (response.ok) {
          await fetchClients();
        } else {
          setError(response.message || "Failed to add client");
        }
      } catch (err: any) {
        console.error("Error adding client:", err);
        setError(err.message || "Failed to add client");
      } finally {
        setLoading(false);
      }
    },
    [accessToken, fetchClients]
  );

  const fetchClientServices = useCallback(
    async (ref_id: string, page: number = 1) => {
      if (!accessToken) {
        setError("No access token available");
        return;
      }

      try {
        setLoadingServices(true);
        setError(null);

        const response = await getClientServiceResults(accessToken, { ref_id, page });
        if (response.ok) {
          setClientServices(response.data.data);
          setServicesCurrentPage(response.data.current_page);
          setServicesTotalPages(response.data.last_page);
        } else {
          setError(response.message || "Failed to fetch client services");
        }
      } catch (err: any) {
        console.error("Error fetching client services:", err);
        setError(err.message || "Failed to fetch client services");
      } finally {
        setLoadingServices(false);
      }
    },
    [accessToken]
  );

  const resetError = () => {
    setError(null);
  };

  const getClientLink = (ref_id: string): string => {
    return generateClientLink(ref_id);
  };

  return (
    <ClientsManagementContext.Provider
      value={{
        clients,
        selectedClient,
        clientServices,
        loading,
        loadingServices,
        error,
        currentPage,
        totalPages,
        totalClients,
        perPage,
        servicesCurrentPage,
        servicesTotalPages,
        addClient,
        fetchClients,
        fetchClientServices,
        setSelectedClient,
        resetError,
        getClientLink,
      }}
    >
      {children}
    </ClientsManagementContext.Provider>
  );
};

export const useClientsManagement = () => {
  const context = useContext(ClientsManagementContext);
  if (!context) {
    throw new Error("useClientsManagement must be used within a ClientsManagementProvider");
  }
  return context;
};
